import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import MapViewer from '../maps/MapViewer';
import useMapWithLocation from '../../hooks/useMapWithLocation';
import { CATEGORY_CONFIG } from '../../config/categories';
import NotificationBanner from './NotificationBanner';
import {
  List, SlidersHorizontal, MapPin, Navigation,
  Clock, AlertCircle, X, ChevronRight, Map,
  Search, Crosshair
} from 'lucide-react';
import './PublicMapViewer.css';

// ─── Analytics ────────────────────────────────────────────────────────────────
const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED === 'true';
const API = process.env.REACT_APP_API_URL;
const LOGO_SRC = '/logo.png';

const analyticsPost = async (path, body) => {
  if (!ANALYTICS_ENABLED) return;
  try {
    await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (_) {}
};

const analyticsPatch = async (path) => {
  if (!ANALYTICS_ENABLED) return;
  try { await fetch(`${API}${path}`, { method: 'PATCH' }); } catch (_) {}
};

const generateUUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

// ─── Category chip row ────────────────────────────────────────────────────────
const CategoryChips = ({ markers, activeCategory, onChange }) => {
  const categories = useMemo(() => {
    const cats = [...new Set(markers.map(m => m.category))].sort();
    return [{ id: 'all', label: 'All', count: markers.length }, ...cats.map(c => ({
      id: c,
      label: (CATEGORY_CONFIG[c] || CATEGORY_CONFIG.other).label,
      emoji: (CATEGORY_CONFIG[c] || CATEGORY_CONFIG.other).emoji,
      count: markers.filter(m => m.category === c).length,
    }))];
  }, [markers]);

  return (
    <div className="pmv-chips">
      {categories.map(cat => (
        <button
          key={cat.id}
          className={`pmv-chip ${activeCategory === cat.id ? 'pmv-chip--active' : ''}`}
          onClick={() => onChange(cat.id)}
        >
          {cat.emoji && <span>{cat.emoji}</span>}
          {cat.label}
          <span className="pmv-chip-count">{cat.count}</span>
        </button>
      ))}
    </div>
  );
};

// ─── Marker detail card ───────────────────────────────────────────────────────
const MarkerCard = ({ marker, userLocation, calculateDistance, formatDistance, onClose }) => {
  const config = CATEGORY_CONFIG[marker.category] || CATEGORY_CONFIG.other;
  const distance = userLocation
    ? calculateDistance(userLocation.lat, userLocation.lng, marker.coordinates[1], marker.coordinates[0])
    : null;

  const dietaryLabels = {
    vegetarian: 'Vegetarian', vegan: 'Vegan',
    glutenFree: 'Gluten-Free', kosher: 'Kosher', halal: 'Halal',
  };

  return (
    <div className="pmv-marker-card">
      <div className="pmv-sheet-handle" />
      <div className="pmv-marker-card-body">
        <button className="pmv-sheet-close" onClick={onClose} aria-label="Close">
          <X size={15} />
        </button>

        <div className="pmv-marker-header">
          <div className="pmv-marker-emoji">{config.emoji}</div>
          <div className="pmv-marker-meta">
            <h2 className="pmv-marker-name">{marker.name}</h2>
            <span className="pmv-marker-cat" style={{ color: config.color }}>
              {config.label}
            </span>
          </div>
          {distance && (
            <div className="pmv-marker-dist">
              <Navigation size={11} />
              {formatDistance(distance)}
            </div>
          )}
        </div>

        {marker.description && (
          <p className="pmv-marker-desc">{marker.description}</p>
        )}

        {marker.openHours && (
          <div className="pmv-marker-detail-row">
            <Clock size={13} />
            <span>{marker.openHours}</span>
          </div>
        )}

        {marker.dietaryOptions && Object.values(marker.dietaryOptions).some(Boolean) && (
          <div className="pmv-marker-badges">
            {Object.entries(marker.dietaryOptions).map(([k, v]) => v ? (
              <span key={k} className="pmv-dietary-badge">{dietaryLabels[k]}</span>
            ) : null)}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── List item ────────────────────────────────────────────────────────────────
const ListItem = ({ marker, userLocation, calculateDistance, formatDistance, onClick }) => {
  const config = CATEGORY_CONFIG[marker.category] || CATEGORY_CONFIG.other;
  const distance = userLocation
    ? calculateDistance(userLocation.lat, userLocation.lng, marker.coordinates[1], marker.coordinates[0])
    : null;

  return (
    <button className="pmv-list-item" onClick={() => onClick(marker)}>
      <div className="pmv-list-icon" style={{ background: config.color + '18' }}>
        <span>{config.emoji}</span>
      </div>
      <div className="pmv-list-info">
        <span className="pmv-list-name">{marker.name}</span>
        {marker.description && (
          <span className="pmv-list-desc">
            {marker.description.length > 60
              ? marker.description.substring(0, 60) + '…'
              : marker.description}
          </span>
        )}
        {marker.openHours && (
          <span className="pmv-list-hours">
            <Clock size={10} /> {marker.openHours}
          </span>
        )}
      </div>
      <div className="pmv-list-right">
        {distance && (
          <span className="pmv-dist-badge">
            {formatDistance(distance)}
          </span>
        )}
        <ChevronRight size={14} className="pmv-list-chevron" />
      </div>
    </button>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const PublicMapViewer = () => {
  const { shareCode } = useParams();
  const [mapData, setMapData] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [showList, setShowList]       = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [filters, setFilters] = useState({ category: 'all', search: '', dietary: 'all' });

  // Analytics refs
  const hasFetchedMap    = useRef(false);
  const hasLoggedVisit   = useRef(false);
  const sessionIdRef     = useRef(null);
  const pingIntervalRef  = useRef(null);
  const viewportDebounce = useRef(null);

  const {
    userLocation,
    locationErrorCode,
    calculateDistance,
    formatDistance,
  } = useMapWithLocation({
    enableTracking: true,
    updateInterval: 3000,
    minDistanceChange: 3,
    showAccuracyCircle: true,
    highAccuracy: true,
  });

  const getCategoryConfig = (cat) => CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.other;

  // Fetch map
  useEffect(() => {
    if (hasFetchedMap.current) return;
    const load = async () => {
      try {
        setLoading(true);
        hasFetchedMap.current = true;
        const [mapRes, markerRes] = await Promise.all([
          fetch(`${API}/api/public/map/${shareCode}`),
          fetch(`${API}/api/public/markers/${shareCode}`),
        ]);
        const mapJson    = await mapRes.json();
        const markerJson = await markerRes.json();
        if (!mapJson.success) throw new Error(mapJson.message || 'Map not found');
        setMapData(mapJson.data.map);
        if (markerJson.success) setMarkers(markerJson.data.markers || []);
      } catch (e) {
        setError(e.message);
        hasFetchedMap.current = false;
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareCode]);

  // Log visit
  useEffect(() => {
    if (hasLoggedVisit.current) return;
    hasLoggedVisit.current = true;
    fetch(`${API}/api/public/visit/${shareCode}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
        referrer: document.referrer.includes('qr') ? 'qr' : 'link',
        userAgent: navigator.userAgent.substring(0, 200),
      }),
    }).catch(() => {});
  }, [shareCode]);

  // Analytics: session
  useEffect(() => {
    if (!ANALYTICS_ENABLED || !mapData) return;
    const existing = sessionStorage.getItem('popmap_session_id');
    const sid = existing || generateUUID();
    if (!existing) sessionStorage.setItem('popmap_session_id', sid);
    sessionIdRef.current = sid;
    const deviceType = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile'
      : /iPad|Tablet/.test(navigator.userAgent) ? 'tablet' : 'desktop';
    const entrySource = document.referrer.includes('qr') ? 'qr'
      : document.referrer ? 'link' : 'direct';
    analyticsPost('/api/analytics/session', {
      sessionId: sid, mapId: mapData.id, eventId: mapData.eventId || null,
      entrySource, deviceType, gpsGranted: false,
    });
  }, [mapData]);

  // Analytics: ping
  useEffect(() => {
    if (!ANALYTICS_ENABLED || !sessionIdRef.current || !mapData || !userLocation) return;
    if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    const ping = () => analyticsPost('/api/analytics/ping', {
      sessionId: sessionIdRef.current, mapId: mapData.id,
      lat: userLocation.lat, lng: userLocation.lng, accuracy: userLocation.accuracy || 0,
    });
    ping();
    pingIntervalRef.current = setInterval(ping, 30000);
    return () => clearInterval(pingIntervalRef.current);
  }, [userLocation, mapData]);

  // Analytics: unload
  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;
    const handler = () => {
      if (sessionIdRef.current) analyticsPatch(`/api/analytics/session/${sessionIdRef.current}`);
    };
    window.addEventListener('beforeunload', handler);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handler();
    });
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Analytics: viewport
  const handleViewportChange = useCallback(({ centerLat, centerLng, zoomLevel }) => {
    if (!ANALYTICS_ENABLED || !sessionIdRef.current || !mapData) return;
    clearTimeout(viewportDebounce.current);
    viewportDebounce.current = setTimeout(() => {
      analyticsPost('/api/analytics/viewport', {
        sessionId: sessionIdRef.current, mapId: mapData.id,
        centerLat, centerLng, zoomLevel,
      });
    }, 2000);
  }, [mapData]);

  const handleMarkerClick = useCallback(async (marker) => {
    try {
      await fetch(`${API}/api/markers/${marker.id}/click`, { method: 'POST' });
      if (ANALYTICS_ENABLED && sessionIdRef.current && mapData) {
        analyticsPost('/api/analytics/click', {
          sessionId: sessionIdRef.current, mapId: mapData.id, markerId: marker.id,
          userLat: userLocation?.lat ?? null, userLng: userLocation?.lng ?? null,
        });
      }
    } catch (_) {}
    setSelectedMarker(marker);
    setShowList(false);
  }, [mapData, userLocation]);

  // Filtered + sorted markers
  const filteredMarkers = useMemo(() => markers.filter(m => {
    if (filters.category !== 'all' && m.category !== filters.category) return false;
    if (filters.dietary !== 'all' && !m.dietaryOptions?.[filters.dietary]) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!`${m.name} ${m.description} ${m.tags?.join(' ')}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [markers, filters]);

  const sortedMarkers = useMemo(() => {
    if (!userLocation) return filteredMarkers;
    return [...filteredMarkers].sort((a, b) => {
      const da = calculateDistance(userLocation.lat, userLocation.lng, a.coordinates[1], a.coordinates[0]);
      const db = calculateDistance(userLocation.lat, userLocation.lng, b.coordinates[1], b.coordinates[0]);
      return da - db;
    });
  }, [filteredMarkers, userLocation, calculateDistance]);

  const hasFilters = filters.category !== 'all' || filters.dietary !== 'all' || filters.search !== '';
  const mapId = mapData?.id || mapData?._id;

  const dietaryLabels = {
    vegetarian: 'Vegetarian', vegan: 'Vegan',
    glutenFree: 'Gluten-Free', kosher: 'Kosher', halal: 'Halal',
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="pmv-splash">
        <div className="pmv-splash-inner">
          <img src={LOGO_SRC} alt="Pop Map" className="pmv-splash-logo" />
          <span className="pmv-splash-wordmark">Pop Map</span>
          <div className="pmv-splash-spinner" />
          <p className="pmv-splash-label">Loading map…</p>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="pmv-splash">
        <div className="pmv-splash-inner">
          <div className="pmv-splash-error-icon"><AlertCircle size={32} /></div>
          <h2 className="pmv-splash-error-title">Map Not Available</h2>
          <p className="pmv-splash-error-msg">{error}</p>
          <button onClick={() => window.location.reload()} className="pmv-splash-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pmv-root">

      {/* Notification banner — fixed, above everything */}
      {mapId && <NotificationBanner mapId={mapId} userLocation={userLocation} />}

      {/* ═══════════════════════════════════════
          MOBILE LAYOUT
      ═══════════════════════════════════════ */}
      <div className="pmv-mobile">

        {/* Map — fills entire screen */}
        <div className="pmv-map-full">
          <MapViewer
            mapData={mapData}
            markers={filteredMarkers}
            userLocation={userLocation}
            height="100%"
            zoom={16}
            showUserLocation={true}
            onMarkerClick={handleMarkerClick}
            onViewportChange={handleViewportChange}
          />
        </div>

        {/* Floating top bar — event name + status */}
        <div className="pmv-float-header">
          <div className="pmv-float-header-inner">
            <img src={LOGO_SRC} alt="" className="pmv-float-logo" />
            <div className="pmv-float-title-wrap">
              <h1 className="pmv-float-title">{mapData.title}</h1>
              {mapData.description && (
                <p className="pmv-float-desc">{mapData.description}</p>
              )}
            </div>
            <span className={`pmv-live-pill ${mapData.isCurrentlyActive ? 'pmv-live-pill--live' : ''}`}>
              <span className="pmv-live-dot" />
              {mapData.isCurrentlyActive ? 'Live' : 'Ended'}
            </span>
          </div>

          {/* Category chips */}
          <CategoryChips
            markers={markers}
            activeCategory={filters.category}
            onChange={cat => setFilters(f => ({ ...f, category: cat }))}
          />
        </div>

        {/* Floating action buttons — bottom right */}
        <div className="pmv-fabs">
          {userLocation && (
            <div className="pmv-fab pmv-fab--gps" title="GPS active">
              <Crosshair size={18} />
            </div>
          )}
          {locationErrorCode === 1 && (
            <div className="pmv-fab pmv-fab--gps-off" title="GPS off">
              <MapPin size={18} />
            </div>
          )}
          <button
            className={`pmv-fab ${showFilters ? 'pmv-fab--active' : ''}`}
            onClick={() => { setShowFilters(true); setShowList(false); }}
            aria-label="Filters"
          >
            <SlidersHorizontal size={18} />
            {hasFilters && <span className="pmv-fab-dot" />}
          </button>
          <button
            className={`pmv-fab ${showList ? 'pmv-fab--active' : ''}`}
            onClick={() => { setShowList(v => !v); setShowFilters(false); }}
            aria-label="List"
          >
            {showList ? <Map size={18} /> : <List size={18} />}
          </button>
        </div>

        {/* Marker count pill — bottom left */}
        <div className="pmv-count-pill">
          {filteredMarkers.length} {filteredMarkers.length === 1 ? 'place' : 'places'}
          {hasFilters && ` · filtered`}
        </div>

        {/* ── List sheet ── */}
        {showList && (
          <>
            <div className="pmv-sheet-overlay" onClick={() => setShowList(false)} />
            <div className="pmv-list-sheet">
              <div className="pmv-sheet-handle" />
              <div className="pmv-list-sheet-header">
                <h3 className="pmv-list-sheet-title">
                  {filteredMarkers.length} {filteredMarkers.length === 1 ? 'place' : 'places'}
                </h3>
                <div className="pmv-list-search-wrap">
                  <Search size={14} className="pmv-list-search-icon" />
                  <input
                    type="text"
                    placeholder="Search places…"
                    value={filters.search}
                    onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                    className="pmv-list-search"
                  />
                  {filters.search && (
                    <button className="pmv-list-search-clear" onClick={() => setFilters(f => ({ ...f, search: '' }))}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
              <div className="pmv-list-scroll">
                {sortedMarkers.length === 0 ? (
                  <div className="pmv-list-empty">
                    <MapPin size={28} strokeWidth={1.5} />
                    <p>No places match your filters</p>
                    {hasFilters && (
                      <button className="pmv-clear-btn" onClick={() => setFilters({ category: 'all', search: '', dietary: 'all' })}>
                        Clear filters
                      </button>
                    )}
                  </div>
                ) : sortedMarkers.map(m => (
                  <ListItem
                    key={m.id}
                    marker={m}
                    userLocation={userLocation}
                    calculateDistance={calculateDistance}
                    formatDistance={formatDistance}
                    onClick={marker => { setSelectedMarker(marker); setShowList(false); }}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Filter sheet ── */}
        {showFilters && (
          <>
            <div className="pmv-sheet-overlay" onClick={() => setShowFilters(false)} />
            <div className="pmv-filter-sheet">
              <div className="pmv-sheet-handle" />
              <div className="pmv-filter-sheet-header">
                <h3 className="pmv-filter-sheet-title">Filters</h3>
                <button className="pmv-sheet-close" onClick={() => setShowFilters(false)}>
                  <X size={15} />
                </button>
              </div>
              <div className="pmv-filter-body">
                <div className="pmv-filter-section">
                  <label className="pmv-filter-label">Search</label>
                  <div className="pmv-filter-search-wrap">
                    <Search size={14} className="pmv-filter-search-icon" />
                    <input
                      type="text"
                      placeholder="Search places…"
                      value={filters.search}
                      onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                      className="pmv-filter-input"
                    />
                  </div>
                </div>
                <div className="pmv-filter-section">
                  <label className="pmv-filter-label">Category</label>
                  <div className="pmv-filter-chips">
                    {[{ id: 'all', label: 'All' }, ...[...new Set(markers.map(m => m.category))].sort().map(c => ({
                      id: c,
                      label: (CATEGORY_CONFIG[c] || CATEGORY_CONFIG.other).label,
                      emoji: (CATEGORY_CONFIG[c] || CATEGORY_CONFIG.other).emoji,
                    }))].map(cat => (
                      <button
                        key={cat.id}
                        className={`pmv-filter-chip ${filters.category === cat.id ? 'pmv-filter-chip--active' : ''}`}
                        onClick={() => setFilters(f => ({ ...f, category: cat.id }))}
                      >
                        {cat.emoji && <span>{cat.emoji}</span>}
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pmv-filter-section">
                  <label className="pmv-filter-label">Dietary</label>
                  <div className="pmv-filter-chips">
                    {[{ id: 'all', label: 'All' }, ...Object.entries(dietaryLabels).map(([k, v]) => ({ id: k, label: v }))].map(opt => (
                      <button
                        key={opt.id}
                        className={`pmv-filter-chip ${filters.dietary === opt.id ? 'pmv-filter-chip--active' : ''}`}
                        onClick={() => setFilters(f => ({ ...f, dietary: opt.id }))}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="pmv-filter-footer">
                {hasFilters && (
                  <button className="pmv-filter-clear" onClick={() => setFilters({ category: 'all', search: '', dietary: 'all' })}>
                    Clear all
                  </button>
                )}
                <button className="pmv-filter-apply" onClick={() => setShowFilters(false)}>
                  Show {filteredMarkers.length} {filteredMarkers.length === 1 ? 'place' : 'places'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Marker detail card ── */}
        {selectedMarker && (
          <>
            <div className="pmv-sheet-overlay" onClick={() => setSelectedMarker(null)} />
            <MarkerCard
              marker={selectedMarker}
              userLocation={userLocation}
              calculateDistance={calculateDistance}
              formatDistance={formatDistance}
              onClose={() => setSelectedMarker(null)}
            />
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════
          DESKTOP LAYOUT
      ═══════════════════════════════════════ */}
      <div className="pmv-desktop">

        {/* Header */}
        <div className="pmv-desktop-header">
          <div className="pmv-desktop-header-inner">
            <div className="pmv-desktop-brand">
              <img src={LOGO_SRC} alt="Pop Map" className="pmv-desktop-logo" />
              <span className="pmv-desktop-wordmark">Pop Map</span>
            </div>
            <div className="pmv-desktop-event-info">
              <h1 className="pmv-desktop-title">{mapData.title}</h1>
              {mapData.description && <p className="pmv-desktop-desc">{mapData.description}</p>}
            </div>
            <div className="pmv-desktop-meta">
              <span className={`pmv-live-pill ${mapData.isCurrentlyActive ? 'pmv-live-pill--live' : ''}`}>
                <span className="pmv-live-dot" />
                {mapData.isCurrentlyActive ? 'Live Now' : 'Ended'}
              </span>
              <span className="pmv-desktop-date">
                {new Date(mapData.startTime).toLocaleDateString()}
              </span>
              {userLocation && (
                <span className="pmv-gps-active-pill">
                  <Crosshair size={11} /> GPS active
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="pmv-desktop-filterbar">
          <div className="pmv-desktop-filterbar-inner">
            <div className="pmv-desktop-search-wrap">
              <Search size={14} className="pmv-desktop-search-icon" />
              <input
                type="text"
                placeholder="Search places…"
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pmv-desktop-search"
              />
            </div>
            <select
              className="pmv-desktop-select"
              value={filters.category}
              onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
            >
              <option value="all">All categories ({markers.length})</option>
              {[...new Set(markers.map(m => m.category))].sort().map(c => {
                const cfg = CATEGORY_CONFIG[c] || CATEGORY_CONFIG.other;
                return (
                  <option key={c} value={c}>
                    {cfg.label} ({markers.filter(m => m.category === c).length})
                  </option>
                );
              })}
            </select>
            <select
              className="pmv-desktop-select"
              value={filters.dietary}
              onChange={e => setFilters(f => ({ ...f, dietary: e.target.value }))}
            >
              <option value="all">All dietary</option>
              {Object.entries(dietaryLabels).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <span className="pmv-desktop-count">
              {filteredMarkers.length} / {markers.length}
            </span>
            {hasFilters && (
              <button className="pmv-desktop-clear" onClick={() => setFilters({ category: 'all', search: '', dietary: 'all' })}>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="pmv-desktop-body">
          <div className="pmv-desktop-map">
            <MapViewer
              mapData={mapData}
              markers={filteredMarkers}
              userLocation={userLocation}
              height="100%"
              zoom={16}
              showUserLocation={true}
              onMarkerClick={handleMarkerClick}
              onViewportChange={handleViewportChange}
            />
          </div>

          {/* Sidebar */}
          <div className="pmv-desktop-sidebar">
            <div className="pmv-sidebar-top">
              <span className="pmv-sidebar-count">
                {filteredMarkers.length} {filteredMarkers.length === 1 ? 'place' : 'places'}
              </span>
              {userLocation && (
                <span className="pmv-sidebar-sorted">Sorted by distance</span>
              )}
            </div>
            <div className="pmv-sidebar-list">
              {sortedMarkers.map(m => (
                <ListItem
                  key={m.id}
                  marker={m}
                  userLocation={userLocation}
                  calculateDistance={calculateDistance}
                  formatDistance={formatDistance}
                  onClick={marker => setSelectedMarker(marker)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Desktop marker detail */}
        {selectedMarker && (
          <>
            <div className="pmv-sheet-overlay pmv-sheet-overlay--desktop" onClick={() => setSelectedMarker(null)} />
            <div className="pmv-desktop-detail">
              <MarkerCard
                marker={selectedMarker}
                userLocation={userLocation}
                calculateDistance={calculateDistance}
                formatDistance={formatDistance}
                onClose={() => setSelectedMarker(null)}
              />
            </div>
          </>
        )}
      </div>

      {/* Powered by footer — desktop only */}
      <div className="pmv-powered">
        <img src={LOGO_SRC} alt="" className="pmv-powered-logo" />
        <span>Powered by Pop Map</span>
      </div>
    </div>
  );
};

export default PublicMapViewer;